/**
 * KarroFin Admin — data access layer.
 *
 * This is the ONLY file that knows whether data is mocked or live. Screens call
 * these functions and never touch axios or the mock module directly, so pointing
 * a screen at a real endpoint is a one-line change here.
 *
 * Promise-based rather than the legacy callback style (request_GET / GET_request
 * in src/main.js). The legacy helpers are left untouched; this module reuses the
 * shared axios instance read-only and attaches the same `token` header the rest
 * of the panel uses.
 */
import axios from "../../helper-axios";
import URLS from "../urls";
import { USE_MOCK, MOCK_LATENCY_MS, ALLOW_REAL_SEND, useMock } from "../config";
import Q from "../mock/queries";
import INS from "../mock/insurance";
import * as normalise from "./normalise";
import {
  PRODUCT_TYPES,
  PRODUCT_FILTER_ALL,
  WHATSAPP_TEMPLATES,
  SMS_TEMPLATES,
} from "../constants";

/* ------------------------------------------------------------- plumbing */

/**
 * Demo/testing switch for the error state: append ?kf_error=1 to the URL and
 * every call rejects, so the ErrorState component can be shown on demand
 * without breaking anything or editing code.
 */
function errorForced() {
  try {
    return window.location.search.indexOf("kf_error=1") !== -1;
  } catch (e) {
    return false;
  }
}

/** Resolves with `value` after the configured mock latency. */
function fake(value) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (errorForced()) {
        reject(new Error("Simulated failure (kf_error=1)"));
        return;
      }
      // Structured-clone-ish copy so a screen mutating its rows cannot
      // corrupt the shared dataset between navigations.
      resolve(value);
    }, MOCK_LATENCY_MS);
  });
}

/**
 * The filters an admin has locked for this user on a demo-data screen.
 *
 * Only the four screens whose rows are generated in the browser from mock/ need
 * this. The six live screens are restricted in the Django queryset, where it
 * cannot be tampered with, and their values are never sent to the browser at
 * all. Read fresh on every call rather than cached, so clearing a restriction
 * takes effect as soon as Base.vue has refreshed it.
 *
 * Answers {} when there is no restriction.
 */
export function demoLocks(screen) {
  try {
    const all =
      JSON.parse(sessionStorage.getItem("view_filter_demo_values")) || {};
    return all[screen] || {};
  } catch (e) {
    return {};
  }
}

/** Force `params` onto the locked values, discarding whatever was asked for. */
function applyDemoLocks(screen, params) {
  const locked = demoLocks(screen);
  return Object.keys(locked).length ? { ...params, ...locked } : params;
}

/** Auth headers, matching the legacy scheme (bare `token`, not Bearer). */
function headers() {
  return {
    token: sessionStorage.getItem("token"),
    "application-type": "web",
  };
}

/**
 * Expand the screens' `date_range: [from, to]` into the `from_date` / `to_date`
 * the server actually reads.
 *
 * Every KarroFin screen holds its date filter as one two-element array, and
 * nothing in cashpo-server has ever read a parameter called `date_range` -- so
 * sent as-is it is silently ignored and the date filter does nothing at all.
 * Done here rather than in each caller so it cannot be forgotten by the next
 * screen to go live, and applied on the way out only: the mock layer reads
 * `date_range` and is not routed through here.
 */
function withDateRange(payload) {
  if (!payload || !payload.date_range) return payload;
  const { date_range, ...rest } = payload;
  if (!Array.isArray(date_range) || date_range.length !== 2) return rest;
  const [from_date, to_date] = date_range;
  return { ...rest, from_date, to_date };
}

/**
 * Query string for a GET, with arrays as repeated BARE keys: `city=A&city=B`.
 *
 * Axios' default serializer appends brackets -- `city[]=A&city[]=B` -- and
 * Django's `request.GET.get('city')` then looks for a key spelled `city`,
 * finds nothing, and skips the filter entirely. That is exactly why Offer
 * Management's City and State did nothing at all: the request went out, the
 * server read no value, and no error was raised at either end. Repeated bare
 * keys are what `request.GET.getlist('city')` reads.
 *
 * Scoped to this wrapper on purpose. helper-axios is the shared instance for
 * the whole legacy panel, so a global paramsSerializer would change every GET
 * the old screens make.
 */
function serializeParams(params) {
  const search = new URLSearchParams();
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item));
    else search.append(key, value);
  });
  return search.toString();
}

/** Live GET. */
function get(url, params) {
  return axios
    .get(url, {
      params: withDateRange(params),
      headers: headers(),
      paramsSerializer: serializeParams,
    })
    .then((r) => r.data);
}

/**
 * A stage-counts reply's bucket list, or a rejection.
 *
 * Both counts endpoints always send every bucket, zeros included, so a reply
 * with no list at all is a failure that happened to arrive as a 200 -- a
 * `success: false` envelope, say. Resolving it to `[]` let the page write an
 * empty list over its last good one and draw the strip with nothing in it.
 * Rejecting sends it down the page's failure path instead, which keeps what it
 * had or falls back to the canonical stage list.
 */
function requireStageCounts(list) {
  if (Array.isArray(list) && list.length) return list;
  throw new Error("Stage counts missing from the reply");
}

/** Live POST. */
function post(url, body, params) {
  return axios
    .post(url, withDateRange(body), { params: withDateRange(params), headers: headers() })
    .then((r) => r.data);
}

/** Live PATCH. */
function patch(url, body) {
  return axios.patch(url, body, { headers: headers() }).then((r) => {
    // Every patch through this module is a write, and any write can move a
    // count. Invalidating here rather than at each call site means a new
    // mutating endpoint cannot forget to do it.
    invalidateCounts();
    return r.data;
  });
}

/* ------------------------------------------------------------ dashboard */

export function getDashboard(params) {
  if (useMock("dashboard")) {
    return fake(Q.dashboard(applyDemoLocks("dashboard", params)));
  }
  // One endpoint, not three. The KPI tiles and the conversion ratios have to be
  // counted off the same population -- "Offers Clicked" IS the numerator of
  // Offer -> Lead -- and separate calls could answer from either side of a write
  // and print a rate that does not match the numbers printed beneath it.
  // No applyDemoLocks on this path: a restricted member's lock is enforced inside
  // the endpoint now, and the server has stopped sending this screen's locked
  // VALUES to the browser at all (is_demo_data, cashpo-server
  // team/view_filters.py). The mock path above keeps the browser-side lock, so
  // flipping this screen back to demo data still restricts it.
  return get(URLS.KARROFIN_DASHBOARD, params).then((env) => env.result || {});
}

/* ---------------------------------------------------------------- leads */

export function getLeads(params) {
  if (USE_MOCK) return fake(Q.leads(params));
  return post(URLS.CUSTOMER_LIST, params);
}

export function getLeadDetail(id) {
  if (USE_MOCK) {
    const detail = Q.leadDetail(id);
    if (!detail) return Promise.reject(new Error("Lead not found"));
    return fake(detail);
  }
  return get(URLS.LEADS_LIST, { id });
}

/* ------------------------------------------------------- campaign URLs */

/* Live only -- there is no mock dataset for campaigns and none of the three
   functions below consults useMock(). A campaign is a real row that has to
   exist for the generated link to resolve, so a demo one would hand the admin
   a URL that 400s the moment anyone opened it. */

/**
 * The product picker inside the Create-campaign-URL dialog.
 *
 * Reuses the SAME endpoint the Products screen runs on rather than adding a
 * campaign-specific listing: `product/admin/product_list/` already reads
 * `search`, `is_active` and `flag_recomended`, which is exactly the three
 * controls the dialog offers. The name mapping is the same one getOffers does
 * below and for the same reason -- an unknown query param is not an error the
 * server reports, it is a filter that silently does nothing.
 *
 * `recommended` arrives as the string "yes"/"no" (or null for All) because it
 * is bound to a v-select; only "yes" narrows the list. Sending
 * flag_recomended=false would be read by Django as the string "false", which is
 * truthy there -- so "no" is filtered in the browser instead.
 */
/* ------------------------------------------------------ template library */
/**
 * The approved-copy library.
 *
 * These call the [IsPanelUser] half of the API -- a person's token, which is
 * what `headers()` already sends. The agent's own two endpoints
 * (templates/available/ and agent/send/) take an internal API key instead and
 * are deliberately absent from this module: the panel has no business calling
 * them, and the server refuses a user token on them anyway.
 *
 * No mock branch. The library is empty rather than absent on a fresh box, and
 * an empty list is the honest answer -- mock templates here would be copy that
 * looks approved and was never reviewed by anyone.
 */
export function getTemplates(params) {
  return get(URLS.TEMPLATE_LIST, params).then((env) => ({
    results: env.results || [],
    total_count: env.total_count || 0,
    total_page_count: env.total_page_count || 1,
  }));
}

export function getTemplate(id) {
  return get(URLS.TEMPLATE_DETAIL + id + "/").then((env) => env.template || {});
}

/** Live at once when nothing is missing, else inactive with inactive_reasons. */
export function createTemplate(payload) {
  return post(URLS.TEMPLATE_CREATE, payload);
}

/**
 * Edit a template. It changes in place and is used as it is from then on; the
 * reply's template carries its new status and any inactive_reasons, so the
 * whole envelope is returned rather than just the template.
 */
export function updateTemplate(id, payload) {
  return patch(URLS.TEMPLATE_DETAIL + id + "/", payload);
}

/**
 * Turn a template on or off by hand. Turning on makes it active only when
 * nothing is missing; otherwise the reply's template says why it is not.
 */
export function switchTemplate(id, on, note) {
  return post(URLS.TEMPLATE_DETAIL + id + "/switch/", { on: !!on, note });
}

export function bulkUploadTemplates(rows, note) {
  return post(URLS.TEMPLATE_BULK, { templates: rows, note });
}

export function getTemplatePerformance(id, days) {
  return get(URLS.TEMPLATE_DETAIL + id + "/performance/", { days });
}


export function getCampaignProductOptions(params) {
  const { search_query, status, recommended, page_length } = params || {};
  return get(URLS.PRODUCT_LIST, {
    page_number: 1,
    // One page deep enough to hold the whole catalogue: the dialog is a
    // tick-list, and a product on page 2 that the admin cannot see is a
    // product they cannot put in the campaign.
    page_length: page_length || 200,
    ...(search_query ? { search: search_query } : {}),
    ...(status ? { is_active: status === "active" } : {}),
    ...(recommended === "yes" ? { flag_recomended: true } : {}),
  }).then((env) => {
    const list = normalise.productList(env);
    // "Not recommended" has no server-side filter, so it is applied here on the
    // page that came back.
    if (recommended === "no") {
      return { ...list, results: list.results.filter((p) => !p.flag_recomended) };
    }
    return list;
  });
}

/**
 * Create a campaign and get its shareable link back.
 *
 * The server returns the finished URL -- it holds the marketing site's address
 * (CAMPAIGN_BASE_URL) and it is the only side that can sign the token.
 */
export function createCampaign(payload) {
  return post(URLS.CAMPAIGN_CREATE, payload).then((env) => {
    invalidateCounts();
    return env.result || {};
  });
}

/**
 * Every campaign, newest first, each with the link it resolves to.
 *
 * The URL comes off the server rather than being rebuilt here: it holds the
 * marketing site's address (CAMPAIGN_BASE_URL) and the token can only be signed
 * server-side. So this list is the one place an admin can get a link back after
 * closing the create dialog.
 */
export function getCampaigns(params) {
  return get(URLS.CAMPAIGN_LIST, params).then((env) => {
    const result = env.result || {};
    return {
      results: result.data || [],
      total_count: result.total_count || 0,
      total_page_count: result.total_page_count || 1,
    };
  });
}

/**
 * Edit a campaign in place — name, products, on/off.
 *
 * The URL is untouched by design and the server sends the whole row back, so
 * the caller can refresh the list without rebuilding a link it must not
 * assemble itself. `campaign_id` is not part of the payload: it is signed into
 * the token, and a new id would mean a new link with the old one dead.
 */
export function updateCampaign(id, payload) {
  return patch(URLS.CAMPAIGN_UPDATE + id + "/", payload).then(
    (env) => env.result || {}
  );
}

/** Switch a campaign on or off. The link survives either way. */
export function setCampaignActive(id, isActive) {
  return patch(URLS.CAMPAIGN_STATUS + id + "/", { is_active: !!isActive });
}

/** Delete a campaign for good. Its link stops resolving; its products are untouched. */
export function deleteCampaign(id) {
  return axios
    .delete(URLS.CAMPAIGN_DELETE + id + "/", { headers: headers() })
    .then((r) => {
      invalidateCounts();
      return r.data;
    });
}

/**
 * How one campaign's dynamic link is doing, partner by partner.
 *
 * `params` is the optional date range ({ date_from, date_to }, YYYY-MM-DD).
 * Resolves to { totals: { clicks, signups }, rows: [{ partner_id, publisher,
 * source, clicks, signups }], offer_clicks: { total, products: [{ product_id,
 * name, clicks }] } } -- rows already sorted by the server, busiest first.
 * Clicks are the deduplicated CampaignClick rows; signups are customers whose
 * first touch carried this campaign, so website signups count too. Offer clicks
 * are offers tapped on the campaign page; they carry no partner, so they come
 * per product rather than on the partner rows.
 */
export function getCampaignStats(id, params) {
  return get(URLS.CAMPAIGN_STATS + id + "/", params).then((env) => {
    const result = env.result || {};
    return {
      totals: result.totals || { clicks: 0, signups: 0 },
      rows: result.rows || [],
      offer_clicks: result.offer_clicks || { total: 0, products: [] },
    };
  });
}

/* --------------------------------------------------------------- offers */

/* Products are live (see LIVE_MODULES in ../config); every other area below
   still uses `USE_MOCK` directly. One consequence worth knowing: ?kf_error=1
   only rejects on the mock path, so that debug switch no longer works here. */

export function getOffers(params) {
  if (useMock("products")) {
    // Split to the same two shapes the live path returns, so the screen has one
    // contract and not two.
    const mocked = Q.offerCatalogue(params);
    return {
      rows: fake({
        results: mocked.results,
        total_page_count: mocked.total_page_count,
        total_count: mocked.total_count,
        page_length: mocked.page_length,
      }),
      counts: fake({
        summary: mocked.summary || null,
        product_counts: mocked.product_counts || null,
        recommended_counts: mocked.recommended_counts || {},
      }),
    };
  }
  // AdminProductListAPIView (cashpo-server products/apis.py) reads `search`,
  // `is_active` and `loan_type`; the screen speaks search_query / status /
  // product_type. Forwarding the screen's names verbatim is not an error the
  // server reports -- unknown query params are ignored, so the filters would
  // silently do nothing and the list would look complete. Hence the mapping.
  const { search_query, status, product_type, page_number, page_length, ...rest } = params;
  // Everything except the product tab, the state and the paging -- `rest`
  // carries the band filters (tenure_min/max, loan_upto_min/max,
  // priority_min/max, flag_recomended) straight through under the names the
  // server reads.
  //
  // TWO bases, because the tabs and the tiles count different things. The
  // product tabs each need a figure for THEIR type, so they count off
  // allBase; the Total / Enabled / Disabled tiles describe the tab you are
  // on, so they count off scopeBase. Sharing one base is what made "Total
  // products" mean the selected type's total, which is the right number under
  // the wrong label the moment an "All" tab exists.
  const allBase = {
    ...rest,
    ...(search_query ? { search: search_query } : {}),
  };
  // "All" is a UI-only value: sent as loan_type it would match no rows at all.
  // Leaving loan_type off is exactly what that tab means.
  const scoped = product_type && product_type !== PRODUCT_FILTER_ALL;
  const scopeBase = scoped ? { ...allBase, loan_type: product_type } : allBase;

  const query = {
    ...scopeBase,
    page_number,
    page_length,
    // Absent means "All"; the server treats an empty value the same way.
    ...(status ? { is_active: status === "active" } : {}),
  };

  // The list endpoint returns no summary, so the tiles above the table are
  // built from count-only probes: one row each, read `total_count`. Cheap, and
  // it keeps the screen's contract identical to the mock's.
  // Memoised on the filters, so paging the table does not recount the tiles --
  // this screen fires seven of these per load, and none of their answers can
  // change when you turn a page.
  const countOnly = (extra) =>
    memoCount(countKey("products", extra), () =>
      get(URLS.PRODUCT_LIST, { page_number: 1, page_length: 1, ...extra })
        .then((env) => (env.result && env.result.total_count) || 0)
        // A tile that fails must not take the table down with it.
        .catch(() => 0)
    );

  // The tiles carry every filter EXCEPT the state, because the state is what
  // they SET. Including it would make them tautological -- pick Disabled and
  // Enabled would read 0, leaving no way back. Same rule the stage tabs on the
  // other screens follow.
  // TWO promises, not one Promise.all over eight requests -- same split as
  // getUsers and getApplications. The rows were being held on screen behind
  // seven count probes, so the table stayed blank until the slowest of eight
  // landed even though its own request had usually arrived first.
  //
  // These probes are cheaper than Offer Management's were -- they hit the
  // product catalogue, which is a small table, not the precheck list -- so this
  // is about eight round trips rather than eight expensive queries. It still
  // means the table waits on seven requests it does not need.
  const counts = Promise.all([
    countOnly(scopeBase),
    countOnly({ ...scopeBase, is_active: true }),
    countOnly(allBase),
    ...PRODUCT_TYPES.map((label) => countOnly({ ...allBase, loan_type: label })),
    recommendedProbes(),
  ])
    .then(([total, active, all, ...rest]) => {
      const recommended = rest.pop();
      const perType = rest;
      return {
        summary: { total, active, inactive: total - active },
        // "All" leads, the way the old screen's Loan Type dropdown started cleared.
        product_counts: [
          { label: PRODUCT_FILTER_ALL, count: all },
          ...PRODUCT_TYPES.map((label, i) => ({ label, count: perType[i] })),
        ],
        recommended_counts: recommended,
      };
    })
    // Losing the tiles must not take the table down with it.
    .catch(() => ({ summary: null, product_counts: null, recommended_counts: {} }));

  return {
    rows: get(URLS.PRODUCT_LIST, query).then(normalise.productList),
    counts,
  };
}

/**
 * How many recommended slots each product type has spent.
 *
 * Deliberately carries NONE of the screen's filters. The cap is a property of
 * the catalogue, not of the rows you happen to be looking at -- counted through
 * a search box it would report a budget with room in it while the real one was
 * full. `is_active: true` because a disabled product holds no slot; that rule
 * lives in products/views.py and this has to agree with it.
 *
 * Answers { "Personal Loan": 2, "Business Loan": 1 }.
 */
function recommendedProbes() {
  const one = (label) =>
    memoCount(countKey("recommended", { loan_type: label }), () =>
    get(URLS.PRODUCT_LIST, {
      page_number: 1,
      page_length: 1,
      loan_type: label,
      flag_recomended: true,
      is_active: true,
    })
      .then((env) => (env.result && env.result.total_count) || 0)
      // A failed probe must not take the table down; it only softens the
      // pre-emptive greying, and the server still refuses the write.
      .catch(() => 0)
    );

  // Memoised because these carry NONE of the screen's filters -- the doc above
  // says so explicitly -- yet they sat inside getOffers' Promise.all and refired
  // on every load, every search keystroke, every page change and every row
  // toggle, for a pair of numbers that only move when someone flips a switch.
  return Promise.all(PRODUCT_TYPES.map(one)).then((counts) => {
    const out = {};
    PRODUCT_TYPES.forEach((label, i) => {
      out[label] = counts[i];
    });
    return out;
  });
}

/** The same figures on their own, for the add/edit form. */
export function getRecommendedCounts() {
  if (useMock("products")) {
    const out = {};
    PRODUCT_TYPES.forEach((label) => {
      out[label] = 0;
    });
    return fake(out);
  }
  return recommendedProbes();
}

export function getOfferDetail(id) {
  if (useMock("products")) {
    const detail = Q.offerDetail(id);
    if (!detail) return Promise.reject(new Error("Offer not found"));
    return fake(detail);
  }
  // There is no single-product endpoint -- the list ignores an `id` param and
  // would quietly return page 1 -- so the row is picked out of a wide page.
  return Promise.all([
    get(URLS.PRODUCT_LIST, { page_number: 1, page_length: 500 }),
    get(URLS.ELIGIBILITY_LIST, { product_id: id }).catch(() => ({ result: [] })),
  ]).then(([listEnv, eligEnv]) => {
    const rows = (listEnv.result && listEnv.result.data) || [];
    const row = rows.find((p) => String(p.id) === String(id));
    if (!row) return Promise.reject(new Error("Product not found"));
    const product = normalise.productRow(row);
    product.eligibility = normalise.eligibilityRow((eligEnv.result || [])[0]);
    // No per-product performance endpoint exists, so the screen hides those
    // sections rather than inventing numbers for them.
    return { product, stats: null, users: [] };
  });
}

export function setOfferActive(id, isActive) {
  if (useMock("products")) return fake(Q.setOfferActive(id, isActive));
  return patch(URLS.PRODUCT_UPDATE + id + "/", { is_active: isActive });
}

export function createOffer(payload) {
  if (useMock("products")) return fake(Q.createOffer(payload));
  return post(URLS.PRODUCT_CREATE, payload).then((env) => {
    invalidateCounts();
    return env;
  });
}

export function updateOffer(id, payload) {
  if (useMock("products")) return fake(Q.updateOffer(id, payload));
  return patch(URLS.PRODUCT_UPDATE + id + "/", payload);
}

/**
 * The logo, always on its own. `headers()` sets no Content-Type, so axios
 * derives multipart/form-data with a boundary from the FormData itself -- and
 * DRF's default parsers accept it, no server change needed.
 */
export function uploadOfferLogo(id, file) {
  if (useMock("products")) return fake(null);
  const body = new FormData();
  // The third argument is load-bearing once a crop is involved. A File carries its
  // own name, but the cropper hands back a Blob, which does not -- and DRF rejects
  // a nameless multipart part as "not a file", so the upload 400s with no clue why.
  body.append("logo", file, file.name || "logo.png");
  return patch(URLS.PRODUCT_UPDATE + id + "/", body);
}

/**
 * Move ONE product to a position defined relative to the others.
 *
 * `payload` is { product_id } plus EXACTLY ONE anchor: before_id, after_id,
 * to_rank (1-based global position, 1 = shown first) or to: "top" | "bottom".
 * Anything else is a 400 from ProductMoveAPIView.
 *
 * An anchor is a product, not a slot, which is why a drag means the same thing
 * on any page and under any filter -- see the docstring on that view in
 * cashpo-server/products/apis.py.
 */
export function moveProduct(payload) {
  if (useMock("products")) return fake({ product_id: payload.product_id, rank: 1 });
  // No invalidateCounts() here, unlike the other writes: a move changes a
  // product's rank and nothing else, so every count is still right. Clearing
  // them cost eight product_list probes per drag, and also threw away every
  // other screen's memoised counts.
  return post(URLS.PRODUCT_MOVE, payload);
}

/** The recommended flag, on its own. Same endpoint the edit form uses. */
export function setOfferRecommended(id, value) {
  if (useMock("products")) return fake(Q.setOfferActive(id, value));
  return updateOffer(id, { flag_recomended: !!value });
}

/**
 * One product's eligibility row.
 *
 * The list endpoint carries none (productRow sets eligibility: null), so the
 * edit form has to ask for it -- and it cannot go through getOfferDetail, which
 * pulls a 500-row page of products to find one row. Answers null when the
 * product has no rules yet, which is a real state.
 */
export function getEligibility(productId) {
  if (useMock("products")) return fake(null);
  return get(URLS.ELIGIBILITY_LIST, { product_id: productId })
    .then((env) => normalise.eligibilityRow((env.result || [])[0]))
    .catch(() => null);
}

/**
 * Eligibility rules, which live in their own table and their own endpoint --
 * ProductUpdateSerializer has no eligibility fields, so this cannot ride along
 * with the product PATCH.
 */
export function updateEligibility(id, payload) {
  if (useMock("products")) return fake(payload);
  return patch(URLS.ELIGIBILITY_UPDATE + id + "/", payload);
}

/**
 * Give a product its first set of eligibility rules.
 *
 * getEligibility answers null for a product that has none -- seeded, imported,
 * or added through Django admin -- and there is nothing to PATCH in that state.
 * Without this the rules typed into the form were dropped on save and the
 * product went on matching nobody. Needs `product` and `product_type` in the
 * payload, which updateEligibility does not: the row does not exist yet, so
 * there is no id carrying that context.
 */
export function createEligibility(payload) {
  if (useMock("products")) return fake(payload);
  return post(URLS.ELIGIBILITY_CREATE, payload).then((env) => {
    invalidateCounts();
    return env;
  });
}

/**
 * Replace a product's serviceable-pincode list from an .xlsx.
 *
 * Multipart, and the field names are the server's: `product` and `file`.
 * headers() sets no Content-Type, so axios derives the multipart boundary from
 * the FormData itself.
 */
export function uploadPincodeSheet(productId, file) {
  if (useMock("products")) return fake(null);
  const body = new FormData();
  body.append("product", productId);
  body.append("file", file);
  return post(URLS.PRODUCT_PINCODE_SHEET, body);
}

/** Is one pincode serviceable for one product? Answers a plain boolean. */
export function checkPincode(productId, pincode) {
  if (useMock("products")) return fake({ is_serviceable: true });
  return post(URLS.PINCODE_SERVICEABILITY, {
    product_id: productId,
    pincode,
  }).then((env) => (env && env.result) || {});
}

/** MasterLoanTypeData, cached: create/update need type ids, not labels. */
let loanTypeCache = null;
export function getLoanTypes() {
  if (useMock("products")) {
    return fake(PRODUCT_TYPES.map((loan_type, i) => ({ id: i + 1, loan_type })));
  }
  if (loanTypeCache) return Promise.resolve(loanTypeCache);
  return get(URLS.LOAN_TYPE_LIST).then((env) => {
    loanTypeCache = env.result || [];
    return loanTypeCache;
  });
}

/**
 * One row per precheck lead. Backs Offer Management.
 *
 * Returns TWO promises, for the same reason getUsers does: the rows must not be
 * held on screen behind the counts. This used to be a single `Promise.all` over
 * FIVE requests -- the page, an exact-count probe, the pipeline buttons and one
 * probe per product tab -- so the table stayed blank until the slowest of the
 * five landed, and three of the five were re-runs of the heaviest endpoint on
 * the screen. Measured against the API on its own: one request took 0.92s,
 * while eight fired together took 8.2s to finish, because the workers serve
 * them a few at a time. Fanning out was making every one of them slower.
 *
 * The header total no longer needs a probe at all -- `precheck_details_fast`
 * returns `total_count`, which it had already computed for its own page maths.
 * The per-product probes remain for now (no endpoint reports those counts yet)
 * but have moved into the `counts` half, so they no longer block the table.
 *
 *   { rows, counts }
 */
export function getApplications(params) {
  if (useMock("applications")) {
    const mocked = Q.applications(params);
    return {
      rows: fake({
        results: mocked.results,
        total_page_count: mocked.total_page_count,
        total_count: mocked.total_count,
      }),
      counts: fake({
        counts: mocked.counts || [],
        product_counts: mocked.product_counts || null,
      }),
    };
  }
  const page = (query) => get(URLS.PRECHECK_LIST, query);

  // The pipeline buttons. `stage` is stripped: a selected button must keep
  // showing its own total, or pressing one would zero the other five.
  const { stage, ...unstaged } = params;
  // Deliberately NOT caught. Losing the buttons still must not take the table
  // down with it -- but that is `rows`' job now, since it is a separate request
  // that settles on its own. Resolving a failure to `[]` made it
  // indistinguishable from a server that genuinely has no buckets, so the
  // screen's success handler wrote that empty list over the good one and the
  // stage strip vanished. A rejection is what lets the caller keep what it had.
  //
  // Memoised like the product probes: the server ignores page_number,
  // page_length and stage here (PRECHECK_INDEX_IGNORED_PARAMS), so paging or
  // pressing a button used to re-ask a question whose answer could not change.
  // A reply with no buckets rejects (requireStageCounts), and memoRequest
  // drops a rejection from the memo, so a bad reply is never served again.
  const stages = memoRequest(countKey("precheck-stages", unstaged), () =>
    get(URLS.PRECHECK_STAGE_COUNTS, unstaged).then((env) =>
      requireStageCounts(((env && env.result) || {}).count)
    )
  );
  const rows = page(params).then((env) =>
    normalise.precheckList(env, undefined, [])
  );
  // The product tabs need a count each, carrying every filter EXCEPT the
  // product itself so pressing one tab never zeroes the other.
  //
  // The selected tab's probe would be the rows request again with
  // page_length=1: same filters, same capped_count on the server. `rows`
  // already carries that number as total_count, so it is read from there and
  // only the OTHER tab is probed. Falls back to the probe if total_count is
  // missing.
  const probe = (label) =>
    exactCount(page, { ...params, product_type: label }, "precheck");
  const perType = PRODUCT_TYPES.map((label) =>
    label === params.product_type
      ? rows
          .then((data) =>
            typeof data.total_count === "number" ? data.total_count : probe(label)
          )
          .catch(() => probe(label))
      : probe(label)
  );

  // Not caught either, for the same reason. Promise.all is all-or-nothing, so a
  // failed stage count also withholds the product-tab numbers gathered beside
  // it -- they keep their last good values, because the assignment in the page
  // simply does not run. Freshening them while the buttons were being blanked
  // was never the better trade.
  const counts = Promise.all([stages, ...perType]).then(
    ([stageCounts, ...typeCounts]) => ({
      counts: stageCounts || [],
      product_counts: PRODUCT_TYPES.map((label, i) => ({
        label,
        count: typeCounts[i] || 0,
      })),
    })
  );

  return { rows, counts };
}

/* ------------------------------------------------------------------ utm */


/**
 * Option lists for the Offer Management filter panel.
 *
 * Live, this is several lookups (city list, lead partners, availed-offer
 * buckets, agents) rather than one endpoint; compose them here so the screen
 * keeps asking one question.
 */
/**
 * The dropdown lists behind the filter bars.
 *
 * Guarded on useMock("users") -- NOT the raw USE_MOCK it used to test. That was
 * the bug: `users` and `applications` are both live, so the tables were reading
 * the real database while these two lists came back as invented demo cities.
 * An admin picked a city that exists nowhere in the data and got an empty table
 * with nothing to explain it.
 *
 * Only cities and states come from the server. The bands (age, salary, CIBIL,
 * tenure, loan_upto) and the small enumerations beside them are UI constructs
 * rather than data, and several screens read them off this same object -- so
 * they are kept and only the two live lists are layered over the top.
 */
export function getFilterOptions() {
  if (useMock("users")) return fake(Q.filterDropdowns());
  return get(URLS.FILTER_OPTIONS)
    .then((env) => ({ ...Q.filterDropdowns(), ...((env && env.result) || {}) }))
    // Empty dropdowns beat mock ones. Falling back to fabricated cities would
    // restore the exact failure this function exists to remove -- better to
    // show nothing to pick than something that silently matches nothing.
    .catch(() => ({ ...Q.filterDropdowns(), cities: [], states: [] }));
}

/* ---------------------------------------------------- count memoisation --- */

/*
 * Count probes are the most expensive thing this module asks the server for.
 * Each one is a COUNT(*) over the whole filtered set, and User Management fires
 * four of them per load -- so at ten lakh customers a single screen load counts
 * a million rows four times over.
 *
 * They are memoised on the filters that can actually change the answer.
 * `page_number` and `page_length` are deliberately NOT part of the key: a count
 * cannot move when you turn the page. Including them is what made paging
 * 1 -> 2 -> 3 re-run every count, three times, for three identical answers.
 *
 * The legacy side has done exactly this for a while -- see `_countCacheKey` in
 * pages/new-customer-pages/index.vue, which builds its key from the same subset
 * and omits the page and the selected tab for the same reason.
 *
 * The PROMISE is cached, not just the value, so several callers asking at once
 * share one request instead of racing four identical ones.
 */
const COUNT_TTL_MS = 30000;
const countMemo = new Map();

/** JSON with object keys in a stable order, so key equality is value equality. */
function stableKey(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(stableKey).join(",") + "]";
  return (
    "{" +
    Object.keys(value)
      .sort()
      .map((k) => JSON.stringify(k) + ":" + stableKey(value[k]))
      .join(",") +
    "}"
  );
}

function countKey(scope, params) {
  const rest = { ...(params || {}) };
  delete rest.page_number;
  delete rest.page_length;
  return scope + "|" + stableKey(rest);
}

function memoCount(key, run) {
  const hit = countMemo.get(key);
  if (hit && Date.now() - hit.at < COUNT_TTL_MS) return hit.promise;
  const promise = run().then((value) => {
    // A probe that failed resolves null (see exactCount's catch). Caching that
    // would pin a transient error in place for the whole TTL.
    if (value === null) countMemo.delete(key);
    return value;
  });
  countMemo.set(key, { at: Date.now(), promise });
  return promise;
}

/**
 * memoCount for a request that REJECTS on failure instead of resolving null.
 *
 * The stage-count strips rely on a rejection to keep their last good numbers
 * (see getApplications), so a failure has to stay a failure -- and must not be
 * served from the memo for the rest of the TTL either.
 */
function memoRequest(key, run) {
  const hit = countMemo.get(key);
  if (hit && Date.now() - hit.at < COUNT_TTL_MS) return hit.promise;
  const promise = run().catch((err) => {
    if (countMemo.get(key) && countMemo.get(key).promise === promise) {
      countMemo.delete(key);
    }
    throw err;
  });
  countMemo.set(key, { at: Date.now(), promise });
  return promise;
}

/**
 * Forget every memoised count.
 *
 * Called by the writes in this module: once a row is toggled, created or
 * deleted, every count that could include it is stale. The TTL above is only a
 * backstop for changes made elsewhere -- by another admin, or by a cron.
 */
export function invalidateCounts() {
  countMemo.clear();
}

/**
 * The exact number of rows a filter matches.
 *
 * Neither the customer nor the precheck list returns a row total -- only a page
 * count. Asking for pages of ONE row makes the page count *be* the row count,
 * which is exact and costs one light request. Deriving it from the normal page
 * count silently overstates whenever the last page is short (21 pages of 10
 * read as 210 for 201 rows).
 */
function exactCount(fetchPage, params, scope) {
  return memoCount(countKey(scope || "count", params), () =>
    fetchPage({ ...params, page_number: 1, page_length: 1 })
    .then((env) => {
      const r = (env && env.result) || env || {};
      const rows = r.data || r.customer_list || [];
      // Django's paginator reports one page even when nothing matched, so an
      // empty first page means zero rows -- not one. Without this a filter
      // matching nothing showed "1" next to an empty table.
      if (!rows.length) return 0;
      return r.total_page_count || 0;
    })
    // A failed count must not take the table down with it.
    .catch(() => null)
  );
}

/**
 * One row per customer, with their single stage. Backs User Management.
 *
 * Returns TWO promises rather than one, and that is the point. The screen used
 * to get five requests joined by a single `Promise.all` -- the list, an exact
 * count, the stage buckets, and one count per product tab -- so the rows, which
 * are usually first to arrive, were held on screen behind the slowest count.
 * That is what made applying a filter look like a hang: the table and the tiles
 * both sat blank until all five landed, and three of the five were re-runs of
 * the heaviest endpoint on the screen.
 *
 * Both numbers those probes existed for are now returned by the two endpoints
 * that were being called anyway -- `total_count` by the list, `product_counts`
 * by the stage counts -- so the probes are gone rather than merely reordered.
 *
 *   { rows, counts }
 *
 * `rows` settles the table; `counts` settles the tiles and the tab numbers. The
 * caller awaits them separately, so neither waits on the other.
 */
export function getUsers(params) {
  if (useMock("users")) {
    // Split to the same two shapes the live path returns, so the screen has one
    // contract and not two. The mock query answers in one object.
    const mocked = Q.users(params);
    return {
      rows: fake({
        results: mocked.results,
        total_page_count: mocked.total_page_count,
        total_count: mocked.total_count,
        count_is_capped: false,
      }),
      counts: fake({
        counts: mocked.counts || [],
        total: mocked.total_count,
        product_counts: mocked.product_counts || null,
      }),
    };
  }
  // POST: this endpoint reads its filters from the body, not the query string.
  const page = (body) => post(URLS.CUSTOMER_LIST, body);

  // The screen calls it `product_type`, like every other KarroFin screen; this
  // endpoint reads `current_loan_type` and nothing else. Renamed here rather
  // than in the page, which is where getProducts translates the same word for
  // its own endpoint too. Before this, the product tabs sent a key the server
  // never looked at -- so pressing one filtered nothing at all.
  const { product_type, ...rest } = params;
  const body = { ...rest, current_loan_type: product_type };

  // The stage buckets above the table. A separate endpoint so a slow count
  // never holds up the rows. `stage` is stripped: a selected bucket must keep
  // showing its own total, or pressing one would zero the other three.
  const { stage, ...unstaged } = body;
  // `total_count` is the real size of the customer base. It cannot be derived by
  // adding the buckets up: the sections overlap, so a customer with a disbursed
  // offer and an open one is counted in two of them (304 against 203 on dev3).
  //
  // `product_counts` carries every filter in this body EXCEPT the product, so
  // the tab numbers move with Search, Date range and City while pressing one tab
  // never zeroes the other. The server drops the product itself -- see
  // karrofin_product_counts in webpanel/views.py.
  // Deliberately NOT caught -- see the same note on getApplications. Losing the
  // counts must not take the table down with it, and it no longer can: `rows` is
  // its own request. What a catch here DID do was resolve a failure to an empty
  // list the page could not tell from a real one, so the stage strip emptied
  // itself and stayed empty.
  //
  // Memoised for the same reason as Offer Management's strip: the server keys
  // this on everything except page_number, page_length and stage, so paging or
  // pressing a bucket cannot change the answer.
  const counts = memoRequest(countKey("user-stages", unstaged), () =>
    post(URLS.USER_STAGE_COUNTS, unstaged).then((env) => ({
      counts: requireStageCounts(env && env.count),
      total: env && typeof env.total_count === "number" ? env.total_count : null,
      product_counts: (env && env.product_counts) || null,
    }))
  );

  return { rows: page(body).then(normalise.customerList), counts };
}

/**
 * Every offer one customer has, across all of their loans.
 *
 * Fetched on demand rather than ridden along on the customer list: the list
 * already prefetches these server-side, but sending them would put every offer
 * of every customer on the page into a payload that exists to draw ten rows.
 * One request when a popup is actually opened is the cheaper trade.
 */
export function getCustomerOffers(userId) {
  if (useMock("users")) return fake({ offers: [], loan_count: 0 });
  return get(URLS.CUSTOMER_OFFERS, { user_id: userId })
    .then(normalise.customerOfferList);
}

/**
 * The six UTM dropdowns: sources, mediums, campaigns, terms, contents,
 * platforms.
 *
 * `.result` is unwrapped here. This endpoint answers
 * `{detail, result: {utm_sources: [...], ...}}` like the rest of the users app,
 * and every caller reads `opts.utm_sources` off the top -- which on the live
 * path was reading it off the envelope and getting `undefined`. The mock hands
 * back the flat object already, which is why this only ever showed up with
 * `users` in LIVE_MODULES.
 *
 * Empty lists rather than a rejection on a shape we do not recognise: a filter
 * with nothing to pick is honest, and beats the screen erroring out over a
 * dropdown.
 */
export function getUtmOptions() {
  // useMock(), not the bare USE_MOCK this used to test -- the same bug, and the
  // same fix, as getFilterOptions() above. All THREE callers are live modules:
  // Offer Management (applications), User Management (users) and the Compose
  // audience picker (notifications). So the bare flag left every Attribution
  // dropdown on invented values -- "sms_blast", "pl_aug_salaried", "banner_a"
  // -- while the tables underneath read the real database. Picking one narrowed
  // the table to nothing with nothing on screen to explain why.
  //
  // The three share store.utmOptions, so whichever screen mounted first also
  // poisoned the other two for the rest of the session.
  //
  // No .catch() fallback here, deliberately, unlike getFilterOptions: every
  // caller writes the resolved value into the shared store, so resolving to six
  // empty lists would cache one transient failure for the whole session. A
  // rejection leaves the store unset and each caller falls back to its own
  // declared [] defaults, so the next screen mount retries.
  if (useMock("users")) return fake(Q.utmDropdown());
  return get(URLS.UTM_DROPDOWN).then((env) => (env && env.result) || env || {});
}

/* -------------------------------------------------------- notifications */

export function getNotifications(params) {
  // useMock(), not the bare USE_MOCK these two used to test: without it,
  // listing the module in LIVE_MODULES would have had no effect and the screen
  // would have stayed on demo data with no sign of it.
  if (useMock("notifications"))
    return fake(Q.notifications(applyDemoLocks("notifications", params)));
  return get(URLS.KF_NOTIFICATION_HISTORY, params).then(normalise.notificationList);
}

export function getNotificationAudience(params) {
  if (useMock("notifications")) return fake(Q.notificationAudience(params));
  return get(URLS.KF_NOTIFICATION_AUDIENCE, params).then(normalise.notificationAudience);
}

/**
 * Template names per channel.
 *
 * The page carries a hardcoded list of the fourteen registered WhatsApp
 * templates. They are real names, so it is right until someone registers a
 * fifteenth and nobody edits the bundle. Falls back to the constant if the call
 * fails -- an unreachable template list should not leave Compose with an empty
 * dropdown and no way to send anything.
 */
export function getNotificationTemplates() {
  if (useMock("notifications"))
    return fake({
      whatsapp: WHATSAPP_TEMPLATES.map((t) => t.name),
      email: [],
      sms: SMS_TEMPLATES,
    });
  return get(URLS.KF_NOTIFICATION_TEMPLATES);
}

/**
 * Send a composed campaign on one channel.
 *
 * The audience filters travel with it, not just a count. The server re-resolves
 * the segment through the same helper that answered the preview, so the number
 * the operator was shown is the number that gets messaged -- passing only
 * `audience_size` would have let the two drift apart silently.
 *
 * Nothing is gated here any more. Whether a given recipient is actually
 * messaged is settings.NOTIFICATION_TEST_RECIPIENTS' decision, server-side,
 * where it also covers anything calling the API without going through this
 * screen. The response reports the split as `queued` and `skipped`.
 */
/**
 * Customers matching a search, for Compose's Individual mode.
 *
 * `channel` travels with the query because reachability is per channel: the
 * same person is reachable by SMS and unreachable by Push, and the picker has
 * to say so while the operator is still choosing rather than in the receipt.
 *
 * Live only -- there is no mock dataset for this. A fabricated customer would
 * hand back an id that no send could resolve, so an unreachable server should
 * show an empty picker, not an invented one.
 */
export function searchNotificationRecipients(params) {
  return get(URLS.KF_NOTIFICATION_RECIPIENTS, params).then(
    (data) => (data && data.results) || []
  );
}

export function sendNotification(payload) {
  if (!ALLOW_REAL_SEND) {
    return fake({
      simulated: true,
      queued: payload.audience_size || 0,
      channel: payload.channel,
      template: payload.template,
      message:
        "Preview only — no message was sent. Sending is disabled in this build.",
    });
  }
  return post(URLS.KF_SEND_NOTIFICATION, payload);
}

/* ----------------------------------------------------------- automation */

/**
 * The Notification History: dry-run proposals and real automation sends,
 * newest first, paged on the server.
 *
 * params: page_number, page_length, date_range ([from, to] -> from_date /
 * to_date via withDateRange), channel, title (array, repeated keys), outcome,
 * search, static (0 keeps only the growth agent's messages), user_id and
 * received (1: one borrower's received messages, for the User history).
 * Resolves to {results, total_count, total_page_count, page_number}.
 */
export function getAutomationHistory(params) {
  if (useMock("automation_history")) return fake(Q.automationHistory(params));
  return get(URLS.KF_AUTOMATION_HISTORY, params).then((data) => ({
    results: (data && data.results) || [],
    total_count: (data && data.total_count) || 0,
    total_page_count: (data && data.total_page_count) || 1,
    page_number: (data && data.page_number) || 1,
  }));
}

/**
 * The Notification History's User history: one row per borrower who received a
 * message, most recently messaged first.
 *
 * params: page_number, page_length, date_range, channel, search, static.
 * Resolves to {results, total_count, total_page_count, page_number,
 * count_is_capped}. Each row: user_id, customer_name, mobile, received_count,
 * would_send_count, last_received_at, last_body (the text as sent),
 * last_subject, last_title, last_channel, channels
 * ([{channel, count}]).
 */
export function getAutomationHistoryUsers(params) {
  if (useMock("automation_history")) return fake(Q.automationHistoryUsers(params));
  return get(URLS.KF_AUTOMATION_HISTORY_USERS, params).then((data) => ({
    results: (data && data.results) || [],
    total_count: (data && data.total_count) || 0,
    total_page_count: (data && data.total_page_count) || 1,
    page_number: (data && data.page_number) || 1,
    count_is_capped: !!(data && data.count_is_capped),
  }));
}

/**
 * The Notification History's Title filter options: every distinct displayed
 * title, narrowed to `date_range` when given and without the static messages'
 * titles when `static` is 0. Resolves to an array of strings.
 */
export function getAutomationHistoryTitles(params) {
  if (useMock("automation_history")) return fake(Q.automationHistoryTitles(params));
  return get(URLS.KF_AUTOMATION_HISTORY_TITLES, params).then(
    (data) => (data && data.results) || []
  );
}

/* --------------------------------------------------------- Agent Tracker */

function listReply(data) {
  return {
    results: (data && data.results) || [],
    total_count: (data && data.total_count) || 0,
    total_page_count: (data && data.total_page_count) || 1,
    page_number: (data && data.page_number) || 1,
    summary: (data && data.summary) || {},
  };
}

/**
 * The Agent Tracker's list: every borrower the growth agent planned a message
 * for or looked at, newest activity first.
 *
 * params: search, page_number, page_length. Resolves to {results, total_count,
 * total_page_count, page_number, summary}; each row has latest_line (their
 * latest day in one sentence), last_day, and counts sent / waiting /
 * no_message / days. summary holds total and test_mode.
 */
export function getGrowthUsers(params) {
  return get(URLS.KF_GROWTH_USERS, params).then(listReply);
}

/**
 * One borrower's days with the growth agent, newest first: each day's message
 * (or none), day_line, simple_reason, time_reason and status.
 *
 * params: user_id. Same reply shape as getGrowthUsers, all days in one page.
 */
export function getGrowthUserHistory(params) {
  return get(URLS.KF_GROWTH_USER_HISTORY, params).then(listReply);
}

/**
 * Missing templates: the borrowers the agent left off a day because no
 * approved message fitted, grouped by borrower type and level. Each row:
 * type / type_label, level_label, borrowers, last_day, needs [{purpose,
 * label}], channels [{channel, label}], tones, why (lines), line.
 *
 * params: days (default 14). Same reply shape as getGrowthUsers.
 */
export function getGrowthGaps(params) {
  return get(URLS.KF_GROWTH_GAPS, params).then(listReply);
}

/**
 * Ask the agent to draft templates for one gap ({type, level, refresh}).
 * Drafting runs in the background; resolves to {state: "running" | "done",
 * drafts, from_agent, gap}. Poll getTemplateSuggestions while running.
 */
export function suggestTemplates(body) {
  return post(URLS.KF_GROWTH_GAPS_SUGGEST, body);
}

/** How a gap's drafting is going ({type, level}); same reply as suggestTemplates. */
export function getTemplateSuggestions(params) {
  return get(URLS.KF_GROWTH_GAPS_SUGGEST, params);
}

/* ------------------------------------------------------------- downloads */

/**
 * Fetch a file and hand it to the browser as a download.
 *
 * `window.open` on the URL would be simpler but cannot carry the `token`
 * header every other call uses -- which is exactly why the one pre-existing
 * "Export CSV" in this app is unauthenticated. So the file comes back as a
 * blob on an authenticated request and is clicked into the downloads folder
 * from an object URL.
 *
 * The filename is built here rather than read from Content-Disposition, so it
 * does not depend on that header being exposed through CORS.
 */
export function downloadFile(url, params, filename, method, options) {
  const config = { headers: headers(), responseType: "blob" };
  const request =
    method === "post"
      ? axios.post(url, params, config)
      : axios.get(url, {
          ...config,
          params,
          // Which array spelling goes on the wire, and it has to be the
          // caller's choice because the two kinds of caller disagree.
          //
          // A legacy screen hands over plain keys (`utm_source`) and relies on
          // axios' default serializer to bracket them, which is what
          // exportDisbursements does. A karrofin screen hands over params built
          // from its filter spec, whose `param` field already spells the
          // bracket (`utm_source[]`) because get() writes keys verbatim -- and
          // running axios' default over one of those produced
          // `utm_source[][]`, so Offer Management's table honoured the UTM
          // filters while its "Download report" quietly ignored them.
          ...(options && options.serialize
            ? { paramsSerializer: serializeParams }
            : {}),
        });

  return request.then((response) => {
    const href = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoked on the next tick: revoking synchronously can cancel the download
    // in some browsers before it has started reading the blob.
    setTimeout(() => URL.revokeObjectURL(href), 0);
    return true;
  });
}

/**
 * An error body on a blob request arrives as a Blob too, so the message has to
 * be read out before it can be shown -- otherwise the user sees "[object Blob]".
 */
export function downloadErrorMessage(err) {
  const data = err && err.response && err.response.data;
  if (data instanceof Blob) {
    return data.text().then((text) => {
      try {
        const parsed = JSON.parse(text);
        return parsed.detail || parsed.message || "Could not build the report";
      } catch (e) {
        return "Could not build the report";
      }
    });
  }
  return Promise.resolve((data && (data.detail || data.message)) || "Could not build the report");
}

/** Today's date, for report filenames. */
function stamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Products, filtered exactly as the screen is. */
export function exportOffers(params) {
  // `rest` must be spread, not dropped: it carries the band filters, and the
  // file has to hold exactly the rows the screen was showing.
  const { search_query, status, product_type, ...rest } = params || {};
  return downloadFile(
    URLS.PRODUCT_EXPORT,
    {
      ...rest,
      ...(search_query ? { search: search_query } : {}),
      ...(status ? { is_active: status === "active" } : {}),
      ...(product_type && product_type !== PRODUCT_FILTER_ALL
        ? { loan_type: product_type }
        : {}),
    },
    `products-${stamp()}.xlsx`
  );
}

/** User Management. POST, because the server helper reads request.data. */
export function exportUsers(params) {
  // Same rename as getUsers: the export runs through the same server helper,
  // so sending `product_type` here would have handed back every lead while the
  // screen was showing one product's worth. The sheet has to be the rows you
  // were looking at.
  //
  // withDateRange for the same reason: downloadFile does not go through
  // get()/post(), so `date_range` went out as-is and the server, which reads
  // only from_date/to_date, exported every signup date while the table showed
  // the last 30 days.
  const { product_type, ...rest } = params || {};
  const body = withDateRange({ ...rest, current_loan_type: product_type });
  return downloadFile(
    URLS.CUSTOMER_EXPORT, body, `user-management-${stamp()}.xlsx`, "post");
}

/** Offer Management. */
export function exportApplications(params) {
  // serialize: these params come from toParams(APPLICATION_FILTERS), whose keys
  // already carry the `[]` that precheck_details_fast/ reads. withDateRange as
  // in exportUsers: without it the export ignored the screen's date range.
  return downloadFile(
    URLS.PRECHECK_EXPORT, withDateRange(params || {}), `offer-management-${stamp()}.xlsx`,
    null, { serialize: true });
}

/* The Disbursement ledger. Takes the screen's own filters minus paging, so the
   file holds exactly the rows on screen -- the server shares the queryset with
   the list endpoint. */
export function exportDisbursements(params) {
  return downloadFile(URLS.DISBURSEMENT_EXPORT, params || {}, `disbursement-${stamp()}.xlsx`);
}

/* The rows of one MIS upload that did not reach an offer, plus the offers still
   flagged disbursed that the file now gives another status -- with the reason
   for each. Carries customer mobiles, so it is a panel-only authenticated
   download rather than a link. */
export function exportReportIssues(report) {
  const name = String(report.report_name || "report").replace(/\.[^.]+$/, "");
  return downloadFile(`${URLS.MIS_REPORT_ISSUES}${report.id}/`, {}, `${name}-issues.csv`);
}

/* --------------------------------------------- lender-reported leads */

/* The MIS rows behind one Leads row: a lead's offer, an MIS-<id> report lead,
   or a CC<id> tracked click -- whichever the row is. Panel-only (mobiles). */
export function getReportHistory(row) {
  let url;
  if (row.row_type === "report_lead") url = `${URLS.REPORT_LEADS}${row.report_lead_id}/history/`;
  else if (row.row_type === "tracked_click") url = `${URLS.CLICK_REPORT_HISTORY}${row.redirect_id}/`;
  else url = `${URLS.OFFER_REPORT_HISTORY}${row.lead_id}/`;
  return get(url).then((env) => env.result || {});
}

/* The offers a report lead could be matched to: the account with its mobile,
   same product first. */
export function getReportLeadCandidates(reportLeadId) {
  return get(`${URLS.REPORT_LEADS}${reportLeadId}/candidates/`).then((env) => env.result || {});
}

/* Match a report lead to an offer: { offer_id, note, force }. A write that can
   move disbursal figures, so cached counts are dropped. */
export function matchReportLead(reportLeadId, body) {
  return post(`${URLS.REPORT_LEADS}${reportLeadId}/match/`, body).then((data) => {
    invalidateCounts();
    return data.result || {};
  });
}

/* ------------------------------------------------------------ insurance */

/**
 * The Insurance screen. On mock while `insurance` stays out of LIVE_MODULES:
 * the dev3 database holds 0 insurance products and 0 insurance leads, and no
 * seeder creates any, so the live path renders an empty state and nothing else.
 * Adding `insurance: true` to LIVE_MODULES in config.js switches all three of
 * these over.
 */
export function getInsuranceList(params) {
  if (useMock("insurance"))
    return fake(INS.insuranceList(applyDemoLocks("insurance", params)));
  return get(URLS.USER_INSURANCE_LIST, params).then(normalise.insuranceList);
}

export function getInsuranceProducts() {
  if (useMock("insurance")) return fake(INS.insuranceProductList());
  return get(URLS.INSURANCE_PRODUCT_LIST).then(normalise.insuranceProductList);
}

export function setInsuranceStatus(id, status) {
  if (useMock("insurance")) return fake(INS.setInsuranceStatus(id, status));
  return patch(URLS.INSURANCE_UPDATE_STATUS + id + "/", { status });
}

/**
 * The screens import this default object, not the named exports -- so a
 * function missing from this map is `undefined` at the call site, and
 * `api.whatever(...)` throws synchronously BEFORE its .then/.catch/.finally are
 * attached. The screen's `loading` flag then never clears and the panel sits on
 * a spinner with nothing in the network tab. Add every new function here.
 */
export default {
  getReportHistory,
  getReportLeadCandidates,
  matchReportLead,
  getDashboard,
  getLeads,
  getLeadDetail,
  getOffers,
  getOfferDetail,
  setOfferActive,
  setOfferRecommended,
  moveProduct,
  getEligibility,
  createEligibility,
  getRecommendedCounts,
  updateEligibility,
  uploadPincodeSheet,
  checkPincode,
  createOffer,
  updateOffer,
  uploadOfferLogo,
  getLoanTypes,
  downloadFile,
  downloadErrorMessage,
  exportOffers,
  exportUsers,
  exportApplications,
  exportDisbursements,
  exportReportIssues,
  getApplications,
  getUsers,
  getCustomerOffers,
  getUtmOptions,
  getFilterOptions,
  getNotifications,
  getNotificationAudience,
  getNotificationTemplates,
  searchNotificationRecipients,
  sendNotification,
  getCampaignProductOptions,
  createCampaign,
  updateCampaign,
  getCampaigns,
  setCampaignActive,
  deleteCampaign,
  getCampaignStats,
  getAutomationHistory,
  getAutomationHistoryUsers,
  getAutomationHistoryTitles,
  getGrowthUsers,
  getGrowthUserHistory,
  getGrowthGaps,
  suggestTemplates,
  getTemplateSuggestions,
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  switchTemplate,
  bulkUploadTemplates,
  getTemplatePerformance,
  getInsuranceList,
  getInsuranceProducts,
  setInsuranceStatus,
};
